import { useState, useEffect } from "react";
import { supabase } from "../lib/supabase";
import {
  Plus, Download, Filter, ChevronRight, X, Search,
  RefreshCw, CreditCard, DollarSign, RepeatIcon,
  CheckSquare, Square, CalendarCheck, BookOpen,
  Printer, AlertCircle, TrendingDown, FileText, Clock
} from "lucide-react";
import * as XLSX from "xlsx";

function money(n, moneda = "CLP") {
  if (moneda === "USD") return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);
  return new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(n || 0);
}
function fmtDate(d) {
  if (!d) return "—";
  return new Date(d + "T12:00:00").toLocaleDateString("es-CL", { day: "2-digit", month: "short", year: "numeric" });
}

const CATEGORIAS = [
  "Software/SaaS","Infraestructura/Cloud","Sueldos","Honorarios",
  "Arriendo","Marketing","Capacitación","Contabilidad","Legal",
  "Equipos","Telecomunicaciones","Seguros","Gastos Generales","Otro"
];
const MEDIOS_PAGO = [
  { v: "transferencia",   l: "Transferencia" },
  { v: "tarjeta_credito", l: "Tarjeta de Crédito" },
  { v: "efectivo",        l: "Efectivo" },
  { v: "cheque",          l: "Cheque" },
];
const ESTADO_COLORS = {
  pendiente:       { bg: "#fff8e1", color: "#e65100", label: "Pendiente" },
  pagado:          { bg: "#e8f5e9", color: "#2e7d32", label: "Pagado" },
  anulado:         { bg: "#fce4ec", color: "#c62828", label: "Anulado" },
  "pendiente TC":  { bg: "#e0f2fe", color: "#0369a1", label: "Pendiente TC" },
};

function EstadoBadge({ estado }) {
  const s = ESTADO_COLORS[estado] || ESTADO_COLORS.pendiente;
  return <span style={{ background: s.bg, color: s.color, borderRadius: 6, padding: "3px 9px", fontSize: 10, fontWeight: 700 }}>{s.label}</span>;
}
function KPI({ label, value, color, sub, icon: Icon }) {
  return (
    <div style={{ flex: 1, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "18px 22px", boxShadow: "0 1px 6px #0000000a" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
        <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase" }}>{label}</div>
        {Icon && <div style={{ width: 32, height: 32, borderRadius: 8, background: color + "15", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon size={14} color={color} /></div>}
      </div>
      <div style={{ fontSize: 21, fontWeight: 800, color: color || "#0d1117" }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

/* ── Filtro Panel ── */
function FiltroPanel({ open, onClose, filtros, onChange, periodos, onLimpiar }) {
  const hayFiltros = Object.values(filtros).some(v => v);
  const inp = { width: "100%", padding: "9px 12px", border: "1.5px solid #e2e8f0", borderRadius: 8, fontSize: 13, fontFamily: "inherit", outline: "none", color: "#0d1117", background: "#fff", boxSizing: "border-box" };
  return (
    <>
      {open && <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "#00000030", zIndex: 150 }} />}
      <div style={{ position: "fixed", top: 0, right: 0, height: "100vh", width: 320, background: "#fff", boxShadow: "-8px 0 32px #00000015", zIndex: 160, display: "flex", flexDirection: "column", transform: open ? "translateX(0)" : "translateX(100%)", transition: "transform .3s cubic-bezier(.4,0,.2,1)", fontFamily: "'DM Sans', sans-serif" }}>
        <div style={{ padding: "20px 24px", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#0d1117" }}>Filtros</div>
            {hayFiltros && <div style={{ fontSize: 11, color: "#29abe2", marginTop: 2 }}>Filtros activos</div>}
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8" }}><X size={20} /></button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px", display: "flex", flexDirection: "column", gap: 18 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: "#64748b", letterSpacing: 1, textTransform: "uppercase", display: "block", marginBottom: 8 }}>Buscar proveedor</label>
            <div style={{ position: "relative" }}>
              <Search size={13} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
              <input value={filtros.busqueda} onChange={e => onChange("busqueda", e.target.value)} placeholder="Nombre o RUT..." style={{ ...inp, paddingLeft: 32 }} />
            </div>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: "#64748b", letterSpacing: 1, textTransform: "uppercase", display: "block", marginBottom: 8 }}>Período</label>
            <select value={filtros.periodo} onChange={e => onChange("periodo", e.target.value)} style={{ ...inp, appearance: "none" }}>
              <option value="">Todos</option>
              {periodos.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: "#64748b", letterSpacing: 1, textTransform: "uppercase", display: "block", marginBottom: 8 }}>Categoría</label>
            <select value={filtros.categoria} onChange={e => onChange("categoria", e.target.value)} style={{ ...inp, appearance: "none" }}>
              <option value="">Todas</option>
              {CATEGORIAS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: "#64748b", letterSpacing: 1, textTransform: "uppercase", display: "block", marginBottom: 8 }}>Estado</label>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {[{ v: "", l: "Todos" },{ v: "pendiente", l: "Pendiente" },{ v: "pagado", l: "Pagado" },{ v: "pendiente TC", l: "Pendiente TC" }].map(({ v, l }) => (
                <button key={v} onClick={() => onChange("estado", v)}
                  style={{ padding: "9px 14px", border: `1.5px solid ${filtros.estado === v ? "#29abe2" : "#e2e8f0"}`, borderRadius: 8, background: filtros.estado === v ? "#29abe210" : "#fff", color: filtros.estado === v ? "#29abe2" : "#374151", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: "#64748b", letterSpacing: 1, textTransform: "uppercase", display: "block", marginBottom: 8 }}>Medio de pago</label>
            <select value={filtros.medioPago} onChange={e => onChange("medioPago", e.target.value)} style={{ ...inp, appearance: "none" }}>
              <option value="">Todos</option>
              {MEDIOS_PAGO.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: "#64748b", letterSpacing: 1, textTransform: "uppercase", display: "block", marginBottom: 8 }}>Rango fechas</label>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div><div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 4 }}>Desde</div><input type="date" value={filtros.fechaDesde} onChange={e => onChange("fechaDesde", e.target.value)} style={inp} /></div>
              <div><div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 4 }}>Hasta</div><input type="date" value={filtros.fechaHasta} onChange={e => onChange("fechaHasta", e.target.value)} style={inp} /></div>
            </div>
          </div>
        </div>
        {hayFiltros && (
          <div style={{ padding: "16px 24px", borderTop: "1px solid #e2e8f0" }}>
            <button onClick={onLimpiar} style={{ width: "100%", padding: "10px", border: "1.5px solid #fecaca", borderRadius: 8, background: "#fef2f2", color: "#dc2626", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Limpiar filtros</button>
          </div>
        )}
      </div>
    </>
  );
}

/* ── Comprobante ── */
function Comprobante({ gasto, onClose }) {
  const [asientos, setAsientos] = useState([]);
  const [detalles, setDetalles] = useState({});
  const [loading, setLoading] = useState(true);
  const TIPO_LABEL = { gasto: "Asiento de Gasto", pago: "Asiento de Pago", ajuste: "Ajuste" };

  useEffect(() => {
    async function cargar() {
      const { data: aa } = await supabase.from("asientos").select("*").eq("gasto_id", gasto.id).order("created_at");
      if (aa?.length) {
        setAsientos(aa);
        const det = {};
        for (const a of aa) {
          const { data: d } = await supabase.from("asiento_detalle").select("*").eq("asiento_id", a.id).order("orden");
          det[a.id] = d || [];
        }
        setDetalles(det);
      }
      setLoading(false);
    }
    cargar();
  }, [gasto.id]);

  return (
    <div style={{ position: "fixed", inset: 0, background: "#00000050", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, fontFamily: "'DM Sans', sans-serif" }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: 16, width: 580, maxHeight: "85vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px #00000025" }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: "18px 24px", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center", background: "#f8fafc", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <BookOpen size={18} color="#29abe2" />
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#0d1117" }}>Comprobantes Contables</div>
              <div style={{ fontSize: 11, color: "#94a3b8" }}>{gasto.razon_proveedor} · {gasto.categoria}</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => window.print()} style={{ background: "#29abe210", border: "1px solid #29abe230", borderRadius: 7, padding: "6px 12px", cursor: "pointer", color: "#29abe2", fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center", gap: 6, fontFamily: "inherit" }}>
              <Printer size={13} /> Imprimir
            </button>
            <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8" }}><X size={18} /></button>
          </div>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
          {loading ? <div style={{ textAlign: "center", color: "#94a3b8", padding: 32 }}>Cargando...</div>
          : asientos.length === 0 ? <div style={{ textAlign: "center", color: "#94a3b8", padding: 32 }}>Sin asientos contables.</div>
          : <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
            {asientos.map((a, idx) => (
              <div key={a.id}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                  <div style={{ background: idx === 0 ? "#fee2e2" : "#dcfce7", border: `1px solid ${idx === 0 ? "#fecaca" : "#bbf7d0"}`, borderRadius: 6, padding: "3px 10px", fontSize: 11, fontWeight: 700, color: idx === 0 ? "#991b1b" : "#166534" }}>
                    {TIPO_LABEL[a.tipo] || a.tipo}
                  </div>
                  <div style={{ fontSize: 12, color: "#64748b" }}>N° {a.numero} · {fmtDate(a.fecha)}</div>
                </div>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: "#0d1117" }}>
                      {["CÓDIGO","CUENTA","DEBE","HABER"].map(h => (
                        <th key={h} style={{ padding: "8px 12px", textAlign: h === "DEBE" || h === "HABER" ? "right" : "left", color: "#fff", fontWeight: 700, fontSize: 10 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(detalles[a.id] || []).map((d, i) => (
                      <tr key={d.id} style={{ borderBottom: "1px solid #f0f4f8", background: i % 2 === 0 ? "#fff" : "#fafbfc" }}>
                        <td style={{ padding: "9px 12px", color: "#29abe2", fontFamily: "monospace", fontWeight: 700, fontSize: 11 }}>{d.cuenta_codigo}</td>
                        <td style={{ padding: "9px 12px", color: "#374151" }}>{d.cuenta_nombre}</td>
                        <td style={{ padding: "9px 12px", textAlign: "right", fontWeight: d.debe > 0 ? 700 : 400, color: d.debe > 0 ? "#0d1117" : "#cbd5e1" }}>{d.debe > 0 ? money(d.debe) : "—"}</td>
                        <td style={{ padding: "9px 12px", textAlign: "right", fontWeight: d.haber > 0 ? 700 : 400, color: d.haber > 0 ? "#0d1117" : "#cbd5e1" }}>{d.haber > 0 ? money(d.haber) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: "#f8fafc", borderTop: "2px solid #e2e8f0" }}>
                      <td colSpan={2} style={{ padding: "9px 12px", fontWeight: 800 }}>TOTALES</td>
                      <td style={{ padding: "9px 12px", textAlign: "right", fontWeight: 800 }}>{money(a.total_debe)}</td>
                      <td style={{ padding: "9px 12px", textAlign: "right", fontWeight: 800 }}>{money(a.total_haber)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ))}
          </div>}
        </div>
      </div>
    </div>
  );
}

/* ── Modal Pago Individual ── */
function PagoGastoModal({ gasto, onClose, onConfirm }) {
  const [fechaPago, setFechaPago] = useState(new Date().toISOString().split("T")[0]);
  const [guardando, setGuardando] = useState(false);

  return (
    <div style={{ position: "fixed", inset: 0, background: "#00000040", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, fontFamily: "'DM Sans', sans-serif" }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: 16, width: 440, boxShadow: "0 20px 60px #00000020" }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: "20px 24px", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <CalendarCheck size={18} color="#10b981" />
            <div style={{ fontSize: 15, fontWeight: 700, color: "#0d1117" }}>Registrar Pago</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8" }}><X size={18} /></button>
        </div>
        <div style={{ padding: "20px 24px" }}>
          {/* Resumen */}
          <div style={{ background: "#f0fdf4", border: "1px solid #6ee7b7", borderRadius: 10, padding: "14px 16px", marginBottom: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#064e3b", marginBottom: 2 }}>{gasto.razon_proveedor}</div>
            <div style={{ fontSize: 11, color: "#065f46", marginBottom: 10 }}>
              {gasto.categoria} {gasto.folio ? `· Folio ${gasto.folio}` : ""} · {fmtDate(gasto.fecha_emision)}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
              {[
                { label: "Neto", valor: gasto.monto_neto },
                { label: "IVA CF", valor: gasto.iva_credito },
                { label: "Total", valor: gasto.monto_total },
              ].map(r => (
                <div key={r.label} style={{ background: "#fff", borderRadius: 7, padding: "8px 10px", textAlign: "center", border: "1px solid #d1fae5" }}>
                  <div style={{ fontSize: 10, color: "#64748b", marginBottom: 2 }}>{r.label}</div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: "#0d1117" }}>{money(r.valor)}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Fecha */}
          <div style={{ marginBottom: 18 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: "#64748b", letterSpacing: 1, textTransform: "uppercase", display: "block", marginBottom: 8 }}>Fecha de pago</label>
            <input type="date" value={fechaPago} onChange={e => setFechaPago(e.target.value)}
              style={{ width: "100%", padding: "10px 14px", border: "1.5px solid #e2e8f0", borderRadius: 8, fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
          </div>

          {/* Info asiento */}
          <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 14px", marginBottom: 18 }}>
            <div style={{ fontSize: 11, color: "#64748b", fontWeight: 700, marginBottom: 6 }}>Asiento contable generado automáticamente:</div>
            <div style={{ fontSize: 11, color: "#374151", lineHeight: 2 }}>
              <span style={{ fontFamily: "monospace", color: "#29abe2", fontWeight: 700 }}>2.1.01</span> Proveedores → <strong>DEBE</strong> {money(gasto.monto_total)} (cancela deuda)<br/>
              <span style={{ fontFamily: "monospace", color: "#29abe2", fontWeight: 700 }}>1.1.01</span> Banco → <strong>HABER</strong> {money(gasto.monto_total)} (salida de dinero)
            </div>
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={onClose} style={{ flex: 1, padding: "10px", border: "1.5px solid #e2e8f0", borderRadius: 8, background: "#fff", color: "#374151", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Cancelar</button>
            <button
              onClick={async () => { setGuardando(true); await onConfirm(gasto, fechaPago); setGuardando(false); }}
              disabled={guardando}
              style={{ flex: 2, padding: "10px", border: "none", borderRadius: 8, background: "#10b981", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
              {guardando ? "Registrando..." : "✓ Registrar pago"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Formulario Nuevo Gasto ── */
function FormGasto({ onClose, onSaved, plantilla = null }) {
  const [form, setForm] = useState({
    rut_proveedor: plantilla?.rut_proveedor || "",
    razon_proveedor: plantilla?.razon_proveedor || "",
    categoria: plantilla?.categoria || "Software/SaaS",
    tipo_doc: "factura",
    folio: "",
    fecha_emision: new Date().toISOString().split("T")[0],
    moneda: plantilla?.moneda || "CLP",
    monto_usd: "",
    tipo_cambio: "",
    monto_neto: "",
    iva_credito: "",
    monto_total: "",
    medio_pago: plantilla?.medio_pago || "transferencia",
    estado: "pendiente",
    notas: "",
  });
  const [guardando, setGuardando] = useState(false);
  const [tcLoading, setTcLoading] = useState(false);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function obtenerTipoCambio() {
    setTcLoading(true);
    try {
      const res = await fetch("https://mindicador.cl/api/dolar");
      const data = await res.json();
      const tc = data.serie[0].valor;
      set("tipo_cambio", tc.toString());
      if (form.monto_usd) {
        const neto = Math.round(parseFloat(form.monto_usd) * tc);
        set("monto_neto", neto.toString());
        set("monto_total", neto.toString());
        set("iva_credito", "0");
      }
    } catch { alert("No se pudo obtener el tipo de cambio"); }
    setTcLoading(false);
  }

  function calcularDesdeUSD() {
    if (form.monto_usd && form.tipo_cambio) {
      const neto = Math.round(parseFloat(form.monto_usd) * parseFloat(form.tipo_cambio));
      set("monto_neto", neto.toString());
      set("monto_total", neto.toString());
      set("iva_credito", "0");
    }
  }

  function calcularTotal() {
    const neto = parseFloat(form.monto_neto) || 0;
    const iva = parseFloat(form.iva_credito) || 0;
    set("monto_total", (neto + iva).toString());
  }

  async function guardar() {
    if (!form.razon_proveedor || !form.monto_neto) { alert("Completa proveedor y monto"); return; }
    setGuardando(true);
    const estadoFinal = form.medio_pago === "tarjeta_credito" ? "pendiente TC" : "pendiente";
    const { error } = await supabase.from("gastos").insert({
      rut_proveedor:   form.rut_proveedor || null,
      razon_proveedor: form.razon_proveedor,
      categoria:       form.categoria,
      tipo_doc:        form.tipo_doc,
      folio:           form.folio || null,
      fecha_emision:   form.fecha_emision,
      moneda:          form.moneda,
      monto_usd:       form.moneda === "USD" ? parseFloat(form.monto_usd) || 0 : 0,
      tipo_cambio:     form.moneda === "USD" ? parseFloat(form.tipo_cambio) || 0 : 0,
      monto_neto:      parseFloat(form.monto_neto) || 0,
      iva_credito:     parseFloat(form.iva_credito) || 0,
      monto_total:     parseFloat(form.monto_total) || parseFloat(form.monto_neto) || 0,
      medio_pago:      form.medio_pago,
      estado:          estadoFinal,
      notas:           form.notas || null,
    });
    if (error) alert("Error: " + error.message);
    else { onSaved(); onClose(); }
    setGuardando(false);
  }

  const labelStyle = { fontSize: 11, fontWeight: 700, color: "#64748b", letterSpacing: 0.5, textTransform: "uppercase", display: "block", marginBottom: 6 };
  const inputStyle = { width: "100%", padding: "9px 12px", border: "1.5px solid #e2e8f0", borderRadius: 8, fontSize: 13, fontFamily: "inherit", outline: "none", color: "#0d1117", boxSizing: "border-box" };

  return (
    <div style={{ position: "fixed", inset: 0, background: "#00000040", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, fontFamily: "'DM Sans', sans-serif" }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: 16, width: 560, maxHeight: "90vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px #00000020" }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: "20px 24px", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#0d1117" }}>{plantilla ? `Gasto desde plantilla: ${plantilla.nombre}` : "Nuevo Gasto"}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8" }}><X size={20} /></button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 12 }}>
            <div><label style={labelStyle}>RUT Proveedor</label><input value={form.rut_proveedor} onChange={e => set("rut_proveedor", e.target.value)} placeholder="76.123.456-7" style={inputStyle} /></div>
            <div><label style={labelStyle}>Razón Social *</label><input value={form.razon_proveedor} onChange={e => set("razon_proveedor", e.target.value)} placeholder="Nombre del proveedor" style={inputStyle} /></div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={labelStyle}>Categoría</label>
              <select value={form.categoria} onChange={e => set("categoria", e.target.value)} style={{ ...inputStyle, appearance: "none" }}>
                {CATEGORIAS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Tipo documento</label>
              <select value={form.tipo_doc} onChange={e => set("tipo_doc", e.target.value)} style={{ ...inputStyle, appearance: "none" }}>
                <option value="factura">Factura</option>
                <option value="boleta">Boleta</option>
                <option value="recibo">Recibo</option>
                <option value="honorarios">Honorarios</option>
                <option value="otro">Otro</option>
              </select>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div><label style={labelStyle}>N° Folio</label><input value={form.folio} onChange={e => set("folio", e.target.value)} placeholder="Opcional" style={inputStyle} /></div>
            <div><label style={labelStyle}>Fecha emisión</label><input type="date" value={form.fecha_emision} onChange={e => set("fecha_emision", e.target.value)} style={inputStyle} /></div>
          </div>
          <div>
            <label style={labelStyle}>Moneda</label>
            <div style={{ display: "flex", gap: 8 }}>
              {["CLP","USD"].map(m => (
                <button key={m} onClick={() => set("moneda", m)}
                  style={{ flex: 1, padding: "9px", border: `1.5px solid ${form.moneda === m ? "#29abe2" : "#e2e8f0"}`, borderRadius: 8, background: form.moneda === m ? "#29abe210" : "#fff", color: form.moneda === m ? "#29abe2" : "#374151", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                  {m === "USD" ? "🇺🇸 USD" : "🇨🇱 CLP"}
                </button>
              ))}
            </div>
          </div>
          {form.moneda === "USD" && (
            <div style={{ background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 10, padding: "14px 16px" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#0369a1", marginBottom: 10 }}>Conversión USD → CLP</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div>
                  <label style={{ ...labelStyle, color: "#0369a1" }}>Monto USD</label>
                  <input type="number" value={form.monto_usd} onChange={e => set("monto_usd", e.target.value)} onBlur={calcularDesdeUSD} placeholder="0.00" style={inputStyle} />
                </div>
                <div>
                  <label style={{ ...labelStyle, color: "#0369a1" }}>Tipo de cambio</label>
                  <div style={{ display: "flex", gap: 6 }}>
                    <input type="number" value={form.tipo_cambio} onChange={e => set("tipo_cambio", e.target.value)} onBlur={calcularDesdeUSD} placeholder="950" style={{ ...inputStyle, flex: 1 }} />
                    <button onClick={obtenerTipoCambio} disabled={tcLoading}
                      style={{ padding: "9px 10px", border: "1.5px solid #bae6fd", borderRadius: 8, background: "#e0f2fe", cursor: "pointer", color: "#0369a1", display: "flex", alignItems: "center" }}>
                      <RefreshCw size={14} />
                    </button>
                  </div>
                </div>
              </div>
              {form.tipo_cambio && <div style={{ fontSize: 11, color: "#0369a1", marginTop: 8 }}>TC: ${parseFloat(form.tipo_cambio).toLocaleString("es-CL")} CLP/USD</div>}
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
            <div><label style={labelStyle}>Monto Neto CLP *</label><input type="number" value={form.monto_neto} onChange={e => set("monto_neto", e.target.value)} onBlur={calcularTotal} placeholder="0" style={inputStyle} /></div>
            <div><label style={labelStyle}>IVA Crédito (19%)</label><input type="number" value={form.iva_credito} onChange={e => set("iva_credito", e.target.value)} onBlur={calcularTotal} placeholder="0" style={inputStyle} /></div>
            <div><label style={labelStyle}>Total CLP</label><input type="number" value={form.monto_total} onChange={e => set("monto_total", e.target.value)} placeholder="0" style={{ ...inputStyle, background: "#f8fafc", fontWeight: 700 }} /></div>
          </div>
          <div>
            <label style={labelStyle}>Medio de pago</label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {MEDIOS_PAGO.map(m => (
                <button key={m.v} onClick={() => set("medio_pago", m.v)}
                  style={{ flex: 1, minWidth: 100, padding: "9px 10px", border: `1.5px solid ${form.medio_pago === m.v ? "#29abe2" : "#e2e8f0"}`, borderRadius: 8, background: form.medio_pago === m.v ? "#29abe210" : "#fff", color: form.medio_pago === m.v ? "#29abe2" : "#374151", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                  {m.v === "tarjeta_credito" ? "💳 " : ""}{m.l}
                </button>
              ))}
            </div>
            {form.medio_pago === "tarjeta_credito" && (
              <div style={{ marginTop: 8, padding: "8px 12px", background: "#e0f2fe", borderRadius: 7, fontSize: 11, color: "#0369a1", fontWeight: 600 }}>
                💳 Se registrará como "Pendiente TC" hasta pagar el estado de cuenta
              </div>
            )}
          </div>
          <div>
            <label style={labelStyle}>Notas</label>
            <textarea value={form.notas} onChange={e => set("notas", e.target.value)} rows={2} placeholder="Descripción adicional..." style={{ ...inputStyle, resize: "none", lineHeight: 1.5 }} />
          </div>
        </div>
        <div style={{ padding: "16px 24px", borderTop: "1px solid #e2e8f0", display: "flex", gap: 10, flexShrink: 0 }}>
          <button onClick={onClose} style={{ flex: 1, padding: "11px", border: "1.5px solid #e2e8f0", borderRadius: 8, background: "#fff", color: "#374151", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Cancelar</button>
          <button onClick={guardar} disabled={guardando} style={{ flex: 2, padding: "11px", border: "none", borderRadius: 8, background: "#0d1117", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
            {guardando ? "Guardando..." : "Guardar gasto"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Plantillas Modal ── */
function PlantillasModal({ onClose, onUsarPlantilla }) {
  const [plantillas, setPlantillas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nueva, setNueva] = useState(false);
  const [form, setForm] = useState({ nombre: "", rut_proveedor: "", razon_proveedor: "", categoria: "Software/SaaS", moneda: "CLP", monto_aprox: "", medio_pago: "tarjeta_credito" });

  useEffect(() => { cargar(); }, []);
  async function cargar() {
    const { data } = await supabase.from("gastos_plantillas").select("*").eq("activa", true).order("nombre");
    setPlantillas(data || []);
    setLoading(false);
  }
  async function guardarPlantilla() {
    await supabase.from("gastos_plantillas").insert({ ...form, monto_aprox: parseFloat(form.monto_aprox) || 0 });
    setNueva(false);
    cargar();
  }
  async function eliminarPlantilla(id) {
    await supabase.from("gastos_plantillas").update({ activa: false }).eq("id", id);
    cargar();
  }
  const inp = { width: "100%", padding: "8px 12px", border: "1.5px solid #e2e8f0", borderRadius: 8, fontSize: 13, fontFamily: "inherit", outline: "none", color: "#0d1117", boxSizing: "border-box" };

  return (
    <div style={{ position: "fixed", inset: 0, background: "#00000040", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, fontFamily: "'DM Sans', sans-serif" }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: 16, width: 560, maxHeight: "85vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px #00000020" }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: "20px 24px", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#0d1117" }}>Gastos Recurrentes</div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => setNueva(!nueva)} style={{ padding: "7px 14px", border: "none", borderRadius: 8, background: "#0d1117", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>+ Nueva plantilla</button>
            <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8" }}><X size={20} /></button>
          </div>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px" }}>
          {nueva && (
            <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "16px", marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#0d1117", marginBottom: 12 }}>Nueva plantilla</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <input value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} placeholder="Nombre (ej: AWS, GitHub, Slack)" style={inp} />
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <input value={form.razon_proveedor} onChange={e => setForm(f => ({ ...f, razon_proveedor: e.target.value }))} placeholder="Proveedor" style={inp} />
                  <select value={form.categoria} onChange={e => setForm(f => ({ ...f, categoria: e.target.value }))} style={{ ...inp, appearance: "none" }}>
                    {CATEGORIAS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <select value={form.moneda} onChange={e => setForm(f => ({ ...f, moneda: e.target.value }))} style={{ ...inp, appearance: "none" }}>
                    <option value="CLP">CLP</option>
                    <option value="USD">USD</option>
                  </select>
                  <input type="number" value={form.monto_aprox} onChange={e => setForm(f => ({ ...f, monto_aprox: e.target.value }))} placeholder="Monto aprox." style={inp} />
                </div>
                <select value={form.medio_pago} onChange={e => setForm(f => ({ ...f, medio_pago: e.target.value }))} style={{ ...inp, appearance: "none" }}>
                  {MEDIOS_PAGO.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
                </select>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => setNueva(false)} style={{ flex: 1, padding: "8px", border: "1.5px solid #e2e8f0", borderRadius: 8, background: "#fff", cursor: "pointer", fontFamily: "inherit", fontSize: 12 }}>Cancelar</button>
                  <button onClick={guardarPlantilla} style={{ flex: 2, padding: "8px", border: "none", borderRadius: 8, background: "#0d1117", color: "#fff", cursor: "pointer", fontFamily: "inherit", fontSize: 12, fontWeight: 700 }}>Guardar</button>
                </div>
              </div>
            </div>
          )}
          {loading ? <div style={{ textAlign: "center", color: "#94a3b8", padding: 32 }}>Cargando...</div>
          : plantillas.length === 0 ? (
            <div style={{ textAlign: "center", color: "#94a3b8", padding: 32 }}>
              <RepeatIcon size={32} style={{ marginBottom: 12, opacity: 0.3 }} />
              <div>Sin plantillas. Crea una para tus gastos recurrentes.</div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {plantillas.map(p => (
                <div key={p.id} style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: "14px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#0d1117" }}>{p.nombre}</div>
                    <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{p.razon_proveedor} · {p.categoria} · {p.moneda === "USD" ? `USD ${p.monto_aprox}` : money(p.monto_aprox)}</div>
                    <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{MEDIOS_PAGO.find(m => m.v === p.medio_pago)?.l}</div>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => { onUsarPlantilla(p); onClose(); }}
                      style={{ padding: "7px 14px", border: "none", borderRadius: 8, background: "#29abe2", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>+ Usar</button>
                    <button onClick={() => eliminarPlantilla(p.id)}
                      style={{ padding: "7px 10px", border: "1px solid #fecaca", borderRadius: 8, background: "#fef2f2", color: "#dc2626", fontSize: 12, cursor: "pointer" }}><X size={12} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Pago TC Masivo ── */
function PagoTCModal({ gastos, onClose, onConfirm }) {
  const [fechaPago, setFechaPago] = useState(new Date().toISOString().split("T")[0]);
  const [guardando, setGuardando] = useState(false);
  const total = gastos.reduce((s, g) => s + (g.monto_total || 0), 0);

  return (
    <div style={{ position: "fixed", inset: 0, background: "#00000040", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, fontFamily: "'DM Sans', sans-serif" }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: 16, width: 460, boxShadow: "0 20px 60px #00000020" }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: "20px 24px", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <CreditCard size={18} color="#29abe2" />
            <div style={{ fontSize: 15, fontWeight: 700, color: "#0d1117" }}>Pago Tarjeta de Crédito</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8" }}><X size={18} /></button>
        </div>
        <div style={{ padding: "20px 24px" }}>
          <div style={{ background: "#e0f2fe", border: "1px solid #bae6fd", borderRadius: 10, padding: "14px 16px", marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: "#0369a1", fontWeight: 600, marginBottom: 4 }}>{gastos.length} gastos seleccionados</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: "#0369a1" }}>{money(total)}</div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: "#64748b", letterSpacing: 1, textTransform: "uppercase", display: "block", marginBottom: 8 }}>Fecha pago estado de cuenta</label>
            <input type="date" value={fechaPago} onChange={e => setFechaPago(e.target.value)}
              style={{ width: "100%", padding: "10px 14px", border: "1.5px solid #e2e8f0", borderRadius: 8, fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
          </div>
          <div style={{ maxHeight: 160, overflowY: "auto", marginBottom: 16, border: "1px solid #e2e8f0", borderRadius: 8 }}>
            {gastos.map(g => (
              <div key={g.id} style={{ padding: "8px 14px", borderBottom: "1px solid #f8fafc", display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                <span style={{ color: "#374151", fontWeight: 600 }}>{g.razon_proveedor} · {g.categoria}</span>
                <span style={{ color: "#0369a1", fontWeight: 700 }}>{money(g.monto_total)}</span>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={onClose} style={{ flex: 1, padding: "10px", border: "1.5px solid #e2e8f0", borderRadius: 8, background: "#fff", color: "#374151", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Cancelar</button>
            <button onClick={async () => { setGuardando(true); await onConfirm(gastos.map(g => g.id), fechaPago); setGuardando(false); onClose(); }} disabled={guardando}
              style={{ flex: 2, padding: "10px", border: "none", borderRadius: 8, background: "#29abe2", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
              {guardando ? "Procesando..." : "Confirmar pago TC"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══ APP PRINCIPAL ══ */
const POR_PAGINA = 20;

export default function Gastos() {
  const [gastos, setGastos]               = useState([]);
  const [loading, setLoading]             = useState(true);
  const [showForm, setShowForm]           = useState(false);
  const [plantillaSeleccionada, setPlantillaSeleccionada] = useState(null);
  const [showPlantillas, setShowPlantillas] = useState(false);
  const [showPagoTC, setShowPagoTC]       = useState(false);
  const [pagoGasto, setPagoGasto]         = useState(null);   // ← NUEVO
  const [comprobante, setComprobante]     = useState(null);
  const [filtroOpen, setFiltroOpen]       = useState(false);
  const [seleccionados, setSeleccionados] = useState(new Set());
  const [pagina, setPagina]               = useState(1);
  const [periodos, setPeriodos]           = useState([]);
  const [filtros, setFiltros]             = useState({ busqueda: "", periodo: "", categoria: "", estado: "", medioPago: "", fechaDesde: "", fechaHasta: "" });

  function setFiltro(k, v) { setFiltros(f => ({ ...f, [k]: v })); setPagina(1); }
  function limpiarFiltros() { setFiltros({ busqueda: "", periodo: "", categoria: "", estado: "", medioPago: "", fechaDesde: "", fechaHasta: "" }); setPagina(1); }

  useEffect(() => { cargar(); }, [filtros.periodo, filtros.categoria, filtros.estado, filtros.medioPago, filtros.fechaDesde, filtros.fechaHasta]);

  async function cargar() {
    setLoading(true);
    let q = supabase.from("gastos").select("*").order("fecha_emision", { ascending: false });
    if (filtros.periodo)    q = q.eq("periodo", filtros.periodo);
    if (filtros.categoria)  q = q.eq("categoria", filtros.categoria);
    if (filtros.estado)     q = q.eq("estado", filtros.estado);
    if (filtros.medioPago)  q = q.eq("medio_pago", filtros.medioPago);
    if (filtros.fechaDesde) q = q.gte("fecha_emision", filtros.fechaDesde);
    if (filtros.fechaHasta) q = q.lte("fecha_emision", filtros.fechaHasta);
    const { data } = await q;
    setGastos(data || []);
    const { data: ps } = await supabase.from("gastos").select("periodo").order("periodo", { ascending: false });
    setPeriodos([...new Set((ps || []).map(p => p.periodo).filter(Boolean))]);
    setLoading(false);
  }

  // ── Pagar gasto individual con asiento contable ──────────
  async function pagarGasto(gasto, fechaPago) {
    // 1. Marcar como pagado
    await supabase.from("gastos")
      .update({ estado: "pagado", fecha_pago: fechaPago })
      .eq("id", gasto.id);

    // 2. Generar asiento de pago
    try {
      const totalPago = gasto.monto_total || gasto.monto_neto || 0;
      const { data: asiento } = await supabase.from("asientos").insert({
        fecha:       fechaPago,
        glosa:       `Pago ${gasto.tipo_doc || "factura"} ${gasto.folio || ""} - ${gasto.razon_proveedor}`.trim(),
        tipo:        "pago",
        gasto_id:    gasto.id,
        periodo:     fechaPago.substring(0, 7),
        estado:      "contabilizado",
        total_debe:  totalPago,
        total_haber: totalPago,
        creado_por:  "sistema",
      }).select().single();

      if (asiento) {
       await supabase.from("asiento_detalle").insert([
  {
    asiento_id:   asiento.id,
    cuenta_codigo: "2.1.01",
    cuenta_nombre: "Proveedores",
    debe:          totalPago,
    haber:         0,
    glosa:         `Pago ${gasto.razon_proveedor}`,
    orden:         1,
  },
  {
    asiento_id:   asiento.id,
    cuenta_codigo: "1.1.02",
    cuenta_nombre: "Banco",
    debe:          0,
    haber:         totalPago,
    glosa:         `Pago ${gasto.razon_proveedor}`,
    orden:         2,
  }
]);
      }
    } catch (e) {
      console.error("Error asiento pago gasto:", e);
    }

    cargar();
    setPagoGasto(null);
  }

  async function pagarTC(ids, fechaPago) {
    for (const id of ids) {
      await supabase.from("gastos").update({ estado: "pagado", fecha_pago: fechaPago }).eq("id", id);
    }
    setGastos(prev => prev.map(g => ids.includes(g.id) ? { ...g, estado: "pagado", fecha_pago: fechaPago } : g));
    setSeleccionados(new Set());
  }

  function toggleSel(id) { setSeleccionados(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; }); }
  function toggleTodos() { setSeleccionados(seleccionados.size === paginados.length ? new Set() : new Set(paginados.map(g => g.id))); }

  function exportarExcel() {
    const rows = filtrados.map(g => ({
      "Proveedor": g.razon_proveedor, "RUT": g.rut_proveedor, "Categoría": g.categoria,
      "Fecha": g.fecha_emision, "Moneda": g.moneda, "USD": g.monto_usd, "TC": g.tipo_cambio,
      "Neto CLP": g.monto_neto, "IVA": g.iva_credito, "Total": g.monto_total,
      "Medio Pago": g.medio_pago, "Estado": g.estado, "Período": g.periodo,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Gastos");
    XLSX.writeFile(wb, `gastos_${new Date().toISOString().split("T")[0]}.xlsx`);
  }

  const filtrados    = gastos.filter(g => {
    if (!filtros.busqueda) return true;
    const q = filtros.busqueda.toLowerCase();
    return (g.razon_proveedor || "").toLowerCase().includes(q) || (g.rut_proveedor || "").toLowerCase().includes(q);
  });
  const paginados    = filtrados.slice((pagina - 1) * POR_PAGINA, pagina * POR_PAGINA);
  const totalPaginas = Math.ceil(filtrados.length / POR_PAGINA);
  const hayFiltros   = Object.values(filtros).some(v => v);
  const totalNeto    = filtrados.reduce((s, g) => s + (g.monto_neto || 0), 0);
  const totalIVA     = filtrados.reduce((s, g) => s + (g.iva_credito || 0), 0);
  const totalBruto   = filtrados.reduce((s, g) => s + (g.monto_total || 0), 0);
  const pendienteTC  = filtrados.filter(g => g.estado === "pendiente TC").length;
  const selArray     = filtrados.filter(g => seleccionados.has(g.id));
  const hayTC        = selArray.some(g => g.medio_pago === "tarjeta_credito");

  return (
    <div style={{ padding: "28px 32px", fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: "#0d1117", margin: 0 }}>Gastos</h1>
          <p style={{ color: "#94a3b8", fontSize: 13, margin: "4px 0 0" }}>Registro de gastos · USD/CLP · Tarjeta de crédito</p>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {seleccionados.size > 0 && hayTC && (
            <button onClick={() => setShowPagoTC(true)}
              style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 16px", border: "none", borderRadius: 8, background: "#29abe2", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
              <CreditCard size={15} /> Pagar TC ({seleccionados.size})
            </button>
          )}
          <button onClick={() => setShowPlantillas(true)}
            style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 16px", border: "1.5px solid #e2e8f0", borderRadius: 8, background: "#fff", color: "#374151", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
            <RepeatIcon size={14} /> Recurrentes
          </button>
          <button onClick={exportarExcel}
            style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 16px", border: "1.5px solid #e2e8f0", borderRadius: 8, background: "#fff", color: "#374151", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
            <Download size={14} /> Excel
          </button>
          <button onClick={() => setFiltroOpen(true)}
            style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 16px", border: `1.5px solid ${hayFiltros ? "#29abe2" : "#e2e8f0"}`, borderRadius: 8, background: hayFiltros ? "#29abe210" : "#fff", color: hayFiltros ? "#29abe2" : "#374151", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
            <Filter size={14} /> Filtros {hayFiltros && <span style={{ background: "#29abe2", color: "#fff", borderRadius: 10, padding: "1px 6px", fontSize: 10 }}>ON</span>}
            <ChevronRight size={13} />
          </button>
          <button onClick={() => { setPlantillaSeleccionada(null); setShowForm(true); }}
            style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 16px", border: "none", borderRadius: 8, background: "#0d1117", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
            <Plus size={15} /> Nuevo gasto
          </button>
        </div>
      </div>

      {pendienteTC > 0 && (
        <div style={{ background: "#e0f2fe", border: "1px solid #bae6fd", borderRadius: 10, padding: "12px 16px", marginBottom: 20, display: "flex", alignItems: "center", gap: 10 }}>
          <AlertCircle size={16} color="#0369a1" />
          <span style={{ fontSize: 13, color: "#0369a1", fontWeight: 600 }}>
            Tienes <strong>{pendienteTC}</strong> gasto(s) pendientes de pago con tarjeta de crédito.
          </span>
        </div>
      )}

      <div style={{ display: "flex", gap: 14, marginBottom: 24 }}>
        <KPI icon={FileText}    label="Gastos"      value={filtrados.length}  color="#ef4444" sub={`${pendienteTC} pendientes TC`} />
        <KPI icon={TrendingDown} label="Neto Total" value={money(totalNeto)}  color="#ef4444" />
        <KPI icon={DollarSign}  label="IVA Crédito" value={money(totalIVA)}   color="#10b981" sub="IVA recuperable" />
        <KPI icon={Clock}       label="Total Bruto" value={money(totalBruto)} color="#8b5cf6" />
      </div>

      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden", boxShadow: "0 1px 6px #0000000a" }}>
        <div style={{ padding: "14px 20px", borderBottom: "1px solid #e2e8f0" }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#0d1117" }}>
            💸 {filtrados.length} gastos{hayFiltros ? " (filtrados)" : ""}
            {seleccionados.size > 0 && <span style={{ marginLeft: 8, color: "#29abe2" }}>· {seleccionados.size} seleccionados</span>}
          </span>
        </div>

        {loading ? (
          <div style={{ padding: 48, textAlign: "center", color: "#94a3b8" }}>Cargando...</div>
        ) : filtrados.length === 0 ? (
          <div style={{ padding: 48, textAlign: "center", color: "#94a3b8" }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>💸</div>
            <div style={{ fontWeight: 700, color: "#64748b" }}>Sin gastos registrados</div>
            <div style={{ fontSize: 12, marginTop: 4 }}>Usa "Nuevo gasto" o carga desde plantillas recurrentes</div>
          </div>
        ) : (
          <>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "#f8fafc" }}>
                    <th style={{ padding: "10px 16px", borderBottom: "1px solid #e2e8f0", width: 40 }}>
                      <button onClick={toggleTodos} style={{ background: "none", border: "none", cursor: "pointer", color: seleccionados.size === paginados.length ? "#29abe2" : "#94a3b8", display: "flex" }}>
                        {seleccionados.size === paginados.length && paginados.length > 0 ? <CheckSquare size={16} /> : <Square size={16} />}
                      </button>
                    </th>
                    {["Proveedor","Categoría","Fecha","Moneda","Neto","IVA CF","Total","Pago","Estado",""].map(h => (
                      <th key={h} style={{ padding: "10px 16px", textAlign: h === "Neto" || h === "IVA CF" || h === "Total" ? "right" : "left", color: "#94a3b8", fontWeight: 700, fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginados.map(g => {
                    const isSel = seleccionados.has(g.id);
                    return (
                      <tr key={g.id} style={{ borderBottom: "1px solid #f8fafc", background: isSel ? "#f0f9ff" : "transparent" }}
                        onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = "#f8fafc"; }}
                        onMouseLeave={e => { if (!isSel) e.currentTarget.style.background = "transparent"; }}>
                        <td style={{ padding: "11px 16px" }} onClick={() => toggleSel(g.id)}>
                          <button style={{ background: "none", border: "none", cursor: "pointer", color: isSel ? "#29abe2" : "#cbd5e1", display: "flex" }}>
                            {isSel ? <CheckSquare size={16} /> : <Square size={16} />}
                          </button>
                        </td>
                        <td style={{ padding: "11px 16px" }}>
                          <div style={{ fontWeight: 600, color: "#0d1117", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g.razon_proveedor || "—"}</div>
                          {g.rut_proveedor && <div style={{ fontSize: 11, color: "#94a3b8", fontFamily: "monospace" }}>{g.rut_proveedor}</div>}
                        </td>
                        <td style={{ padding: "11px 16px" }}>
                          <span style={{ background: "#f1f5f9", color: "#475569", borderRadius: 5, padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>{g.categoria}</span>
                        </td>
                        <td style={{ padding: "11px 16px", color: "#64748b", whiteSpace: "nowrap" }}>{fmtDate(g.fecha_emision)}</td>
                        <td style={{ padding: "11px 16px" }}>
                          {g.moneda === "USD" ? (
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 700, color: "#0369a1" }}>USD {g.monto_usd}</div>
                              <div style={{ fontSize: 10, color: "#94a3b8" }}>TC: {g.tipo_cambio}</div>
                            </div>
                          ) : <span style={{ fontSize: 11, color: "#94a3b8" }}>CLP</span>}
                        </td>
                        <td style={{ padding: "11px 16px", textAlign: "right", fontWeight: 600 }}>{money(g.monto_neto)}</td>
                        <td style={{ padding: "11px 16px", textAlign: "right", color: "#10b981", fontWeight: 600 }}>{money(g.iva_credito)}</td>
                        <td style={{ padding: "11px 16px", textAlign: "right", fontWeight: 800 }}>{money(g.monto_total)}</td>
                        <td style={{ padding: "11px 16px" }}>
                          {g.medio_pago === "tarjeta_credito"
                            ? <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "#29abe2", fontWeight: 600 }}><CreditCard size={12} /> TC</span>
                            : <span style={{ fontSize: 11, color: "#94a3b8" }}>{MEDIOS_PAGO.find(m => m.v === g.medio_pago)?.l}</span>}
                        </td>
                        <td style={{ padding: "11px 16px" }}><EstadoBadge estado={g.estado} /></td>
                        <td style={{ padding: "11px 16px", whiteSpace: "nowrap" }}>
                          {/* Botón pagar — solo si está pendiente */}
                          {(g.estado === "pendiente" || g.estado === "pendiente TC") && (
                            <button
                              onClick={() => setPagoGasto(g)}
                              title="Registrar pago"
                              style={{ background: "#f0fdf4", border: "1px solid #6ee7b7", borderRadius: 6, cursor: "pointer", color: "#10b981", padding: "4px 8px", fontSize: 11, fontWeight: 700, marginRight: 4, display: "inline-flex", alignItems: "center", gap: 3 }}>
                              <CalendarCheck size={12} /> Pagar
                            </button>
                          )}
                          <button onClick={() => setComprobante(g)} style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8" }} title="Ver comprobante">
                            <BookOpen size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {totalPaginas > 1 && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", borderTop: "1px solid #e2e8f0" }}>
                <div style={{ fontSize: 12, color: "#94a3b8" }}>
                  {Math.min((pagina - 1) * POR_PAGINA + 1, filtrados.length)}–{Math.min(pagina * POR_PAGINA, filtrados.length)} de {filtrados.length}
                </div>
                <div style={{ display: "flex", gap: 4 }}>
                  <button onClick={() => setPagina(p => Math.max(1, p - 1))} disabled={pagina === 1}
                    style={{ padding: "6px 12px", border: "1px solid #e2e8f0", borderRadius: 7, background: "#fff", cursor: pagina === 1 ? "not-allowed" : "pointer", color: pagina === 1 ? "#cbd5e1" : "#374151", fontSize: 12, fontFamily: "inherit" }}>‹</button>
                  <span style={{ padding: "6px 14px", border: "1px solid #29abe2", borderRadius: 7, background: "#29abe2", color: "#fff", fontSize: 12, fontWeight: 700 }}>{pagina}/{totalPaginas}</span>
                  <button onClick={() => setPagina(p => Math.min(totalPaginas, p + 1))} disabled={pagina === totalPaginas}
                    style={{ padding: "6px 12px", border: "1px solid #e2e8f0", borderRadius: 7, background: "#fff", cursor: pagina === totalPaginas ? "not-allowed" : "pointer", color: pagina === totalPaginas ? "#cbd5e1" : "#374151", fontSize: 12, fontFamily: "inherit" }}>›</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modales */}
      <FiltroPanel open={filtroOpen} onClose={() => setFiltroOpen(false)} filtros={filtros} onChange={setFiltro} periodos={periodos} onLimpiar={limpiarFiltros} />
      {showForm     && <FormGasto onClose={() => { setShowForm(false); setPlantillaSeleccionada(null); }} onSaved={cargar} plantilla={plantillaSeleccionada} />}
      {showPlantillas && <PlantillasModal onClose={() => setShowPlantillas(false)} onUsarPlantilla={p => { setPlantillaSeleccionada(p); setShowForm(true); }} />}
      {showPagoTC   && <PagoTCModal gastos={selArray} onClose={() => setShowPagoTC(false)} onConfirm={pagarTC} />}
      {pagoGasto    && <PagoGastoModal gasto={pagoGasto} onClose={() => setPagoGasto(null)} onConfirm={pagarGasto} />}
      {comprobante  && <Comprobante gasto={comprobante} onClose={() => setComprobante(null)} />}

      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');`}</style>
    </div>
  );
}
